/**
 * Reveal allo scroll — entrata scaglionata dei blocchi di contenuto.
 *
 * ScrollTrigger fa solo il rilevamento: quando il contenitore entra in viewport
 * gli aggiunge .is-inview, e l'animazione (fade + risalita, con lo stagger dato
 * dal transition-delay) è interamente in CSS — src/sass/theme/_reveal.scss.
 * Nessun tween per elemento: il JS assegna un indice e togglea una classe,
 * quindi il costo runtime resta piatto anche con centinaia di blocchi.
 *
 * Markup:
 *   [data-reveal]         contenitore; i figli diretti entrano in sequenza
 *   [data-reveal="fade"]  variante senza risalita (sezioni con dots-pattern)
 *   [data-reveal-skip]    figlio escluso (decorativo, o già animato da un altro modulo)
 *
 * I contenitori possono annidarsi: una griglia dentro un blocco marcato è a sua
 * volta [data-reveal], così le card si scaglionano una per una invece di entrare
 * come un unico pannello.
 *
 * Si scaglionano solo i figli diretti, mai discendenti arbitrari: lo stato
 * iniziale nascosto deve poter essere espresso in CSS (selettore >) per essere
 * attivo già al primo paint, prima che questo modulo giri.
 *
 * Sostituisce related-cards.js: le griglie realizzazioni/archivio sono ora
 * normali contenitori reveal.
 */
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

( function () {
	'use strict';

	const blocks = document.querySelectorAll( '[data-reveal]' );
	if ( ! blocks.length ) {
		return;
	}

	if ( window.matchMedia && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches ) {
		return; // il CSS lascia tutto visibile e immobile.
	}

	gsap.registerPlugin( ScrollTrigger );

	const viewportHeight = () => window.innerHeight || document.documentElement.clientHeight;

	// Blocchi già visibili al caricamento: partono subito, senza aspettare lo
	// scroll. clamp() da solo non basta — limita lo start a zero solo per i
	// blocchi molto in alto, mentre uno a 700px con viewport 800 resterebbe
	// fermo finché non si scorre di una manciata di pixel.
	const immediate = [];

	blocks.forEach( ( block ) => {
		let index = 0;
		Array.prototype.forEach.call( block.children, ( item ) => {
			if ( item.hasAttribute( 'data-reveal-skip' ) ) {
				return; // non consuma un posto nella sequenza.
			}
			item.style.setProperty( '--am-reveal-i', index );
			index += 1;
		} );

		if ( ! index ) {
			return; // niente da scaglionare.
		}

		const rect = block.getBoundingClientRect();
		if ( rect.top < viewportHeight() && rect.bottom > 0 ) {
			immediate.push( block );
			return;
		}

		ScrollTrigger.create( {
			trigger: block,
			// clamp() tiene lo start dentro l'intervallo di scroll utile per i
			// blocchi vicini agli estremi della pagina.
			start: 'clamp(top 85%)',
			once: true,
			onEnter: () => block.classList.add( 'is-inview' ),
		} );
	} );

	// Un frame di respiro: garantisce che lo stato iniziale (e gli indici di
	// stagger appena scritti) siano stati applicati prima di far partire la
	// transizione, altrimenti il browser potrebbe accorpare le due modifiche
	// e l'entrata verrebbe saltata.
	if ( immediate.length ) {
		requestAnimationFrame( () => {
			immediate.forEach( ( block ) => block.classList.add( 'is-inview' ) );
		} );
	}
}() );
